import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useGetBots } from "shared/api/requests/bots";
import { useGetChannels } from "shared/api/requests/channels";
import CutText from "shared/components/CutText";
import MainItemMenu from "shared/components/MainItemMenu";
import { PathsE } from "shared/routes";
import { FilterField } from "shared/types/filters";
import { formatDateToHuman } from "shared/utils/formattings";
import { LinksElementT } from "../../api/types/links";
import MuiIcon from "../../../components/MuiIcon";
import { Box, Flex, Text } from "@chakra-ui/react";
import useGetColors from "../../hooks/useGetColors";
import CopyButton from "shared/components/CopyButton";

export const isHiddenOptions: { value: string; label: string }[] = [
  {
    value: "true",
    label: "true",
  },
  {
    value: "false",
    label: "false",
  },
];

export const useGetFilterFields = (): FilterField[] => {
  const { t } = useTranslation();

  return useMemo(
    () => [
      {
        type: "text",
        name: "url",
        defaultValue: "",
        placeholder: t("forms.enterUrl"),
        label: t("subscriptions.table.url"),
        size: "lg",
      },
      {
        type: "text",
        name: "source",
        defaultValue: "",
        placeholder: t("subscriptions.table.source"),
        label: t("subscriptions.table.source"),
        size: "lg",
      },
      {
        type: "async-select",
        isMulti: true,
        name: "channelIds",
        defaultValue: [],
        placeholder: t("forms.selectChannels"),
        optionsQuery: useGetChannels as FixMeLater,
        label: t("fields.channel", { count: 0 }),
        size: "lg",
        labelName: "name",
        valueName: "id",
        fullWidth: true,
      },
      {
        type: "async-select",
        isMulti: true,
        name: "botIds",
        defaultValue: [],
        placeholder: t("forms.selectBots"),
        optionsQuery: useGetBots as FixMeLater,
        label: t("bots.bots"),
        size: "lg",
        labelName: "name",
        valueName: "id",
        fullWidth: true,
      },
      {
        type: "date-range",
        name: "createdAt",
        defaultValue: [],
        label: t("subscribers.table.createdAt"),
        size: "lg",
      },
      {
        type: "select",
        name: "isHidden",
        defaultValue: "false",
        label: t("links.table.showHiddenLinks"),
        placeholder: t("users.table.showHiddenLinks"),
        options: isHiddenOptions,
        size: "lg",
      },
    ],
    [t],
  );
};

export const useGetGetBreadcrumbs = () => {
  const { t } = useTranslation();
  return [
    {
      path: PathsE.HOME,
      text: t("general.home"),
    },
  ];
};


interface Params {
    handleHidden: (linkId: string) => void
}

export const useGetColumns = ({ handleHidden }: Params) => {
  const { t } = useTranslation();
  const columnHelper = createColumnHelper<LinksElementT>();
  const { brand500 } = useGetColors();

  return useMemo(
    () =>
      [
        columnHelper.accessor("id", {
          header: t("subscriptions.table.id"),
          id: "id",
          cell: ({ getValue }) => (
            <CutText fontWeight="700">{getValue()}</CutText>
          ),
          meta: {
            isSorting: false,
            styles: {
              sizes: {
                width: "90px",
                maxW: "90px",
              },
            },
          },
        }),
        columnHelper.accessor((row) => row.model.name ?? "", {
          header: t("subscriptions.table.resource"),
          id: "resource",
          cell: ({ getValue }) => (
            <Flex alignItems="center" justifyContent="space-between" gap={2}>
              <Box mr="4px">
                <MuiIcon name="group" color={brand500} />
              </Box>
              <Text as="span" mr="4px">
                {getValue()}
              </Text>
            </Flex>
          ),
        }),
        columnHelper.accessor("source", {
          header: t("subscriptions.table.source"),
          id: "source",
        }),
        columnHelper.accessor("buyer", {
          header: t("subscriptions.table.buyer"),
          id: "buyer",
        }),
        columnHelper.accessor("link", {
          header: t("links.link"),
          id: "link",
          cell: ({ row, getValue }) => (
            <Box textAlign="center" alignItems="center" display="flex" gap={4}>
              <Box
                textAlign="center"
                alignItems="center"
                display="flex"
                gap={2}
              >
                {t("links.link")}
                <CopyButton text={getValue()} />
              </Box>
              |
              <Box
                textAlign="center"
                alignItems="center"
                display="flex"
                gap={2}
              >
                {t("links.table.deepLink")}
                <CopyButton text={row.original.deepLink} />
              </Box>
            </Box>
          ),
          meta: {
            styles: {
              sizes: {
                width: "240px",
                maxW: "240px",
              },
            },
          },
        }),
        columnHelper.accessor("fbPixel", {
          header: "FB Pixel/Token",
          id: "fbPixel",
          cell: ({ getValue }) => {
            const value = getValue();
            if (!value) return null;
            return (
              <Box
                textAlign="center"
                alignItems="center"
                display="flex"
                gap={2}
              >
                <CutText>{value}</CutText>
                <CopyButton text={value} />
              </Box>
            );
          },
        }),
        columnHelper.accessor("subscribersCount", {
          header: t("subscribers.subscribers"),
          id: "subscribersCount",
        }),
        columnHelper.accessor("messagesCount", {
          header: t("subscribers.table.messages"),
          id: "messagesCount",
        }),
        columnHelper.accessor("createdAt", {
          header: t("subscriptions.table.createdAt"),
          id: "createdAt",
          cell: ({ getValue }) => formatDateToHuman(getValue(), false),
          meta: {
            isNumeric: true,
            styles: {
              sizes: {
                width: "120px",
              },
            },
          },
        }),
        columnHelper.accessor("updatedAt", {
          header: t("subscriptions.table.updatedAt"),
          id: "updatedAt",
          cell: ({ getValue }) => formatDateToHuman(getValue(), false),
          meta: {
            isNumeric: true,
            styles: {
              sizes: {
                width: "120px",
              },
            },
          },
        }),
        columnHelper.accessor("id", {
          header: "",
          id: "actions",
          cell: ({ row }) => (
            <MainItemMenu
              data={row.original}
              isHideCopyUUID
              handleHidden={handleHidden}
              isHidden={row.original.isHidden}
              viewLink={PathsE.SHOW_LINK}
              pathsParams={{ linkId: row.original.id }}
            />
          ),
          meta: {
            styles: {
              sizes: {
                width: "44px",
              },
            },
          },
        }),
      ] as ColumnDef<LinksElementT>[],
    [t, handleHidden],
  );
};
